import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getElementsBySceneId } from '../../../../api/element';
import { getCurrentUserInfo } from '../../../../utils/userUtils';
import { SceneInfo, SceneDataStatus, SceneWithUserNames } from '../../../../types/scene.types';
import { sortScenes } from '../Utils/SceneCodeUtils';
import { SortConfig } from '../BreakdownMain.types';
import { useSceneUpdates } from '../../../../hooks/dataUpdates/useSceneUpdates';
import { useElementUpdates } from '../../../../hooks/dataUpdates/useElementUpdates';
import { useDataCache } from '../../../../contexts/BreakdownDataCacheContext';

/**
 * CRUD 操作 Hook
 * 统一管理场景的创建、读取、更新、删除操作
 */
export const useSceneCRUD = (
  breakdownId: string | undefined,
  scenes: SceneWithUserNames[],
  setScenes: React.Dispatch<React.SetStateAction<SceneWithUserNames[]>>,
  selectedSceneId: string | null,
  setSelectedSceneId: React.Dispatch<React.SetStateAction<string | null>>,
  setSelectedSceneData: React.Dispatch<React.SetStateAction<Partial<SceneInfo> | undefined>>,
  sortConfig: SortConfig,
  setSceneLocationDisplayTexts: React.Dispatch<React.SetStateAction<{ [sceneId: string]: string }>>,
  onSceneCreated?: (sceneId: string) => void, // 回调函数，用于在创建场景后激活编辑状态
  onHomeBreakdownMetadataRefresh?: () => void,
) => {
  const { t } = useTranslation();
  const { createSceneData, deleteSceneData } = useSceneUpdates();
  const { batchCreateElementRelationsData } = useElementUpdates();
  const { getBreakdownCache, setSceneCache } = useDataCache();

  /**
   * 添加新场次（头部按钮）
   */
  const handleAddScene = useCallback(async () => {
    if (!breakdownId) return;

    try {
      const userInfo = getCurrentUserInfo();
      const newSceneId = await createSceneData(breakdownId, {
        sceneName: t('breakdown.untitledNewScene'),
        sceneBelongBreakdownID: breakdownId,
        sceneCreatorID: userInfo.userID,
        sceneDataStatus: SceneDataStatus.NORMAL
      });

      if (newSceneId) {
        // 从缓存获取最新数据并更新本地状态
        const cache = getBreakdownCache(breakdownId);
        if (cache) {
          const newScene = cache.scenes.find(s => s.sceneID === newSceneId);
          if (newScene) {
            // 应用排序
            const sortedScenes = sortScenes(cache.scenes, sortConfig.key, sortConfig.direction);
            setScenes(sortedScenes);
            // 自动选中新创建的场次
            setSelectedSceneId(newSceneId);
          }
        }
        onHomeBreakdownMetadataRefresh?.();
      }
    } catch (error) {
      console.error(t('breakdown.addSceneFailed'), error);
      alert(t('breakdown.addSceneFailed') + t('common.labelSeparator') + (error instanceof Error ? error.message : t('common.unknownError')));
    }
  }, [breakdownId, createSceneData, getBreakdownCache, setScenes, sortConfig, setSelectedSceneId, t, onHomeBreakdownMetadataRefresh]);

  /**
   * 在表格中添加新场次（场号表头的+按钮）
   * 创建占位行，在选中行下方插入
   * 只有当用户输入信息后才真正创建数据
   */
  const handleAddSceneInTable = useCallback(() => {
    if (!breakdownId || !selectedSceneId) return;

    const userInfo = getCurrentUserInfo();
    // 生成临时 ID
    const tempSceneId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // 创建占位行数据
    const placeholderScene: SceneWithUserNames = {
      sceneID: tempSceneId,
      sceneName: t('breakdown.untitledNewScene'),
      sceneBelongBreakdownID: breakdownId,
      sceneCreatorID: userInfo.userID,
      sceneDataStatus: SceneDataStatus.NORMAL,
      sceneCode: undefined,
      sceneCreateTime: new Date().toISOString(),
      sceneUpdateTime: new Date().toISOString(),
    } as SceneWithUserNames;

    // 找到选中场次在当前 scenes 列表中的索引
    const selectedIndex = scenes.findIndex(scene => scene.sceneID === selectedSceneId);
    
    if (selectedIndex !== -1) {
      // 在选中行下方插入占位行
      const beforeSelected = scenes.slice(0, selectedIndex + 1);
      const afterSelected = scenes.slice(selectedIndex + 1);
      const updatedScenes = [...beforeSelected, placeholderScene, ...afterSelected];
      
      setScenes(updatedScenes);
      setSelectedSceneId(tempSceneId);
      // 设置 selectedSceneData，供 SceneDetails 使用
      setSelectedSceneData(placeholderScene);
      
      // 使用统一导航系统激活新行的场号输入框编辑状态
      if (onSceneCreated) {
        // 等待 DOM 更新完成
        setTimeout(() => {
          onSceneCreated(tempSceneId);
        }, 100);
      } else {
        // 后备方案：直接查找 DOM 元素
        setTimeout(() => {
          const inputElement = document.querySelector(`input[data-scene-id="${tempSceneId}"][data-field="sceneCode"]`) as HTMLInputElement;
          if (inputElement) {
            inputElement.focus();
            inputElement.select();
          }
        }, 100);
      }
    } else {
      // 如果找不到选中行，添加到末尾
      setScenes([...scenes, placeholderScene]);
      setSelectedSceneId(tempSceneId);
      // 设置 selectedSceneData，供 SceneDetails 使用
      setSelectedSceneData(placeholderScene);
      
      // 使用统一导航系统激活新行的场号输入框编辑状态
      if (onSceneCreated) {
        // 等待 DOM 更新完成
        setTimeout(() => {
          onSceneCreated(tempSceneId);
        }, 100);
      } else {
        // 后备方案：直接查找 DOM 元素
        setTimeout(() => {
          const inputElement = document.querySelector(`input[data-scene-id="${tempSceneId}"][data-field="sceneCode"]`) as HTMLInputElement;
          if (inputElement) {
            inputElement.focus();
            inputElement.select();
          }
        }, 100);
      }
    }
  }, [breakdownId, selectedSceneId, scenes, setScenes, setSelectedSceneId, setSelectedSceneData, t, onSceneCreated]);

  /**
   * 在表格末尾添加场次（底部按钮）
   */
  const handleAddSceneInTableEnd = useCallback(async (count: number) => {
    if (!breakdownId) return;

    try {
      const userInfo = getCurrentUserInfo();
      const newSceneIds: string[] = [];

      // 批量创建场次
      for (let i = 0; i < count; i++) {
        try {
          const newSceneId = await createSceneData(breakdownId, {
            sceneCode: '', // 空白场号，等待用户编辑
            sceneName: t('breakdown.untitledNewScene'),
            sceneBelongBreakdownID: breakdownId,
            sceneCreatorID: userInfo.userID,
            sceneDataStatus: SceneDataStatus.NORMAL
          });
          if (newSceneId) {
            newSceneIds.push(newSceneId);
          }
        } catch (error) {
          console.error(`创建第 ${i + 1} 个场次失败:`, error);
        }
      }

      if (newSceneIds.length > 0) {
        // 从缓存获取最新数据
        const cache = getBreakdownCache(breakdownId);
        if (cache) {
          const sortedScenes = sortScenes(cache.scenes, sortConfig.key, sortConfig.direction);
          setScenes(sortedScenes);

          // 使用统一导航系统激活第一个新建行的场号编辑状态
          if (onSceneCreated) {
            // 等待 DOM 更新完成
            setTimeout(() => {
              onSceneCreated(newSceneIds[0]);
            }, 100);
          } else {
            // 后备方案：直接查找 DOM 元素
            setTimeout(() => {
              const firstNewSceneId = newSceneIds[0];
              const inputElement = document.querySelector(`input[data-scene-id="${firstNewSceneId}"][data-field="sceneCode"]`) as HTMLInputElement;
              if (inputElement) {
                inputElement.focus();
                inputElement.select();
              }
            }, 100);
          }
        }
        onHomeBreakdownMetadataRefresh?.();
      }
    } catch (error) {
      console.error(t('breakdown.addSceneFailed'), error);
      alert(t('breakdown.addSceneFailed') + t('common.labelSeparator') + (error instanceof Error ? error.message : t('common.unknownError')));
    }
  }, [breakdownId, createSceneData, getBreakdownCache, setScenes, sortConfig, t, onSceneCreated, onHomeBreakdownMetadataRefresh]);

  /**
   * 删除场次并跳转到下一行
   * 注意：此函数不再包含确认对话框，确认对话框应在调用此函数之前显示
   */
  const handleDeleteScene = useCallback(async (sceneId: string) => {
    if (!breakdownId) return;
    
    try {
      // 找到要删除的场次在列表中的索引
      const deleteIndex = scenes.findIndex(scene => scene.sceneID === sceneId);
      
      await deleteSceneData(sceneId, breakdownId);
      
      // 从缓存获取最新数据，并按当前排序配置排序以保持用户选择的排序方式
      const cache = getBreakdownCache(breakdownId);
      if (cache) {
        const updatedScenes = sortScenes(
          cache.scenes,
          sortConfig.key,
          sortConfig.direction
        );
        setScenes(updatedScenes);
        
        // 确定下一个要选中的场次ID
        let nextSceneId: string | null = null;
        
        if (updatedScenes.length > 0) {
          // 如果删除的不是最后一个，选中下一个
          if (deleteIndex < updatedScenes.length) {
            nextSceneId = updatedScenes[deleteIndex].sceneID;
          } else {
            // 如果删除的是最后一个，选中新的最后一个
            nextSceneId = updatedScenes[updatedScenes.length - 1].sceneID;
          }
        }
        
        // 更新选中状态
        setSelectedSceneId(nextSceneId);
        setSelectedSceneData(undefined); // 重置外部数据状态
      }
      onHomeBreakdownMetadataRefresh?.();
    } catch (error) {
      console.error('删除场次失败:', error);
      alert(t('useSceneCRUD.deleteSceneFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
    }
  }, [breakdownId, scenes, sortConfig, deleteSceneData, getBreakdownCache, setScenes, setSelectedSceneId, setSelectedSceneData, t, onHomeBreakdownMetadataRefresh]);

  /**
   * 复制场次
   */
  const handleCopyScene = useCallback(async (sceneId: string) => {
    if (!breakdownId) return;

    try {
      const userInfo = getCurrentUserInfo();
      
      // 获取当前场次信息
      const currentScene = scenes.find(s => s.sceneID === sceneId);
      if (!currentScene) {
        console.error('找不到要复制的场次');
        return;
      }

      // 生成唯一的新场号（不添加后缀，直接使用原场号）
      const generateUniqueSceneCode = (baseCode: string): string => {
        // 直接返回原场号，不添加任何后缀
        return baseCode || '';
      };

      const newSceneCode = generateUniqueSceneCode(currentScene.sceneCode || '');

      // 创建新场次
      const newSceneId = await createSceneData(breakdownId, {
        sceneCode: newSceneCode,
        sceneName: currentScene.sceneName ? `${currentScene.sceneName}-COPYSCENE` : '-COPYSCENE',
        sceneBelongBreakdownID: breakdownId,
        sceneFilmID: currentScene.sceneFilmID,
        sceneCreatorID: userInfo.userID,
        sceneDataStatus: SceneDataStatus.NORMAL,
        scenePeriod: currentScene.scenePeriod,
        sceneBudgetAmount: currentScene.sceneBudgetAmount,
        scenePlot: currentScene.scenePlot,
        scenePlotSummary: undefined, // 复制场次时，日历摘要保持为空
        scenePlotDay: currentScene.scenePlotDay,
        scenePage: currentScene.scenePage,
        // 规范化场次页数：确保是数字类型，最多1位小数
        scenePagesNumber: currentScene.scenePagesNumber !== null && currentScene.scenePagesNumber !== undefined
          ? Math.round(Number(currentScene.scenePagesNumber) * 10) / 10
          : undefined,
        sceneLocation: currentScene.sceneLocation,
        sceneSet: currentScene.sceneSet,
        sceneInteriorExterior: currentScene.sceneInteriorExterior,
        sceneDayNight: currentScene.sceneDayNight
      });

      if (newSceneId) {
        // 获取原场次的所有场次元素
        const elementsResponse = await getElementsBySceneId(sceneId);
        const originalElements = elementsResponse.data;

        // 批量创建场次元素关系（将原场次元素关联到新场次）
        if (originalElements.length > 0) {
          const relations = originalElements.map((item: any) => ({
            elementID: item.elementID,  // 使用原场次元素的ID
            sceneID: newSceneId,  // 关联到新场次
            elementQtyInScene: item.elementQtyInScene,  // 复制数量字段
          }));

          // 批量创建关系
          await batchCreateElementRelationsData(breakdownId, relations);
        }

        // 等待缓存更新完成（确保 ElementRelationCache 已更新）
        await new Promise(resolve => setTimeout(resolve, 100));

        // 从缓存获取最新数据
        const cache = getBreakdownCache(breakdownId);
        if (cache) {
          // 应用排序
          const sortedScenes = sortScenes(cache.scenes, sortConfig.key, sortConfig.direction);
          setScenes(sortedScenes);

          // 确保 SceneCache 也被更新（useElementsData 需要从 SceneCache 获取 breakdownId）
          const newScene = sortedScenes.find(s => s.sceneID === newSceneId);
          if (newScene && setSceneCache) {
            setSceneCache(newSceneId, {
              sceneInfo: {
                ...newScene,
                sceneBelongBreakdownID: breakdownId,
              } as any,
            });
          }

          // 打开新创建的场次
          setSelectedSceneId(newSceneId);
          setSelectedSceneData(undefined);

          // 使用统一导航系统激活新复制行的场号编辑状态
          if (onSceneCreated) {
            // 等待 DOM 更新和缓存同步完成（增加延迟以确保 useElementsData 已订阅到缓存更新）
            setTimeout(() => {
              onSceneCreated(newSceneId);
            }, 200);
          }

          console.log('场次复制成功');
          onHomeBreakdownMetadataRefresh?.();
        }
      } else {
        console.error('创建场次失败');
        alert(t('useSceneCRUD.duplicateSceneFailed'));
      }
    } catch (error) {
      console.error('复制场次失败:', error);
      alert(t('useSceneCRUD.duplicateSceneFailedWithError') + (error instanceof Error ? error.message : t('common.unknownError')));
    }
  }, [breakdownId, scenes, sortConfig, createSceneData, batchCreateElementRelationsData, getBreakdownCache, setScenes, setSelectedSceneId, setSelectedSceneData, onSceneCreated, setSceneCache, t, onHomeBreakdownMetadataRefresh]);

  /**
   * 处理场次详情页的数据更新回调
   */
  const handleSceneDetailUpdate = useCallback((sceneId: string, updatedData: Partial<SceneInfo>) => {
    // 如果更新了场号（sceneCode）且当前排序配置是按场号排序的，需要重新排序
    const shouldResort = updatedData.sceneCode !== undefined && sortConfig.key === 'sceneCode' && sortConfig.direction;
    
    // 同步更新场次表中的数据
    setScenes(prev => {
      const updated = prev.map(scene =>
        scene.sceneID === sceneId
          ? { ...scene, ...updatedData }
          : scene
      );
      
      // 如果更新了场号（sceneCode）且当前排序配置是按场号排序的，需要重新排序
      if (shouldResort) {
        const sorted = sortScenes(updated, sortConfig.key, sortConfig.direction);
        return sorted;
      }
      
      return updated;
    });
    
    // 如果更新的是当前选中场次，同时更新 selectedSceneData（保持一致性）
    if (sceneId === selectedSceneId) {
      setSelectedSceneData(prev => ({ ...prev, ...updatedData }));
    }
    
    // 如果更新了地点相关字段，同步更新地点显示文本缓存
    if (updatedData.sceneLocation !== undefined) {
      setSceneLocationDisplayTexts(prev => ({
        ...prev,
        [sceneId]: updatedData.sceneLocation || ''
      }));
    }
  }, [setScenes, setSceneLocationDisplayTexts, sortConfig, selectedSceneId, setSelectedSceneData]);

  return {
    handleAddScene,
    handleAddSceneInTable,
    handleAddSceneInTableEnd,
    handleDeleteScene,
    handleCopyScene,
    handleSceneDetailUpdate,
    createSceneData, // 导出 createSceneData，供 useSceneUpdates 使用
  };
};

